import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  writeBatch,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  HomepageProductSection, 
  HeroSectionData, 
  PromotionalBannerData,
  StorefrontOperations,
  createHomepageProductSectionId,
  createEmptyHomepageProductSection,
  createEmptyHeroSectionData,
  createEmptyPromotionalBannerData
} from '../types';

class StorefrontService implements StorefrontOperations {
  private readonly HOMEPAGE_SECTIONS_COLLECTION = 'homepageProductSections';
  private readonly HERO_SECTION_DOC = 'heroSection';
  private readonly PROMOTIONAL_BANNER_DOC = 'promotionalBanner';

  // Homepage Product Sections
  async getHomepageProductSections(): Promise<HomepageProductSection[]> {
    try {
      const sectionsRef = collection(db, this.HOMEPAGE_SECTIONS_COLLECTION);
      const q = query(sectionsRef, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const sections = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as HomepageProductSection;
      });
      return sections.sort((a, b) => a.order - b.order);
    } catch (error) {
      console.error('Error fetching homepage product sections:', error);
      throw new Error('Failed to fetch homepage product sections');
    }
  }

  async createHomepageProductSection(section: Partial<HomepageProductSection>): Promise<HomepageProductSection> {
    try {
      const id = createHomepageProductSectionId();
      const sectionData = {
        ...createEmptyHomepageProductSection(),
        ...section,
        id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = doc(db, this.HOMEPAGE_SECTIONS_COLLECTION, id);
      await setDoc(docRef, sectionData);

      return {
        ...sectionData,
        createdAt: new Date(),
        updatedAt: new Date()
      } as HomepageProductSection;
    } catch (error) {
      console.error('Error creating homepage product section:', error);
      throw new Error('Failed to create homepage product section');
    }
  }

  async updateHomepageProductSection(id: string, updates: Partial<HomepageProductSection>): Promise<void> {
    try {
      const docRef = doc(db, this.HOMEPAGE_SECTIONS_COLLECTION, id);
      // Firestore's updateDoc can throw (e.g. "indexOf" on undefined) when a value is undefined,
      // and Date objects from the form should not be written. Only pass defined, serializable fields.
      const sanitized: Record<string, unknown> = { updatedAt: serverTimestamp() };
      for (const [key, value] of Object.entries(updates)) {
        if (key === 'createdAt' || key === 'updatedAt') continue;
        if (value === undefined) continue;
        if (value instanceof Date) continue;
        sanitized[key] = value;
      }
      await updateDoc(docRef, sanitized);
    } catch (error) {
      console.error('Error updating homepage product section:', error);
      throw new Error('Failed to update homepage product section');
    }
  }

  async deleteHomepageProductSection(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.HOMEPAGE_SECTIONS_COLLECTION, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting homepage product section:', error);
      throw new Error('Failed to delete homepage product section');
    }
  }

  async reorderHomepageProductSections(sections: HomepageProductSection[]): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      sections.forEach((section, index) => {
        const docRef = doc(db, this.HOMEPAGE_SECTIONS_COLLECTION, section.id);
        batch.update(docRef, {
          order: index,
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
    } catch (error) {
      console.error('Error reordering homepage product sections:', error);
      throw new Error('Failed to reorder homepage product sections');
    }
  }

  // Hero Section
  async getHeroSectionData(): Promise<HeroSectionData | null> {
    try {
      const docRef = doc(db, 'storefront', this.HERO_SECTION_DOC);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as HeroSectionData;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching hero section data:', error);
      throw new Error('Failed to fetch hero section data');
    }
  }

  async updateHeroSectionData(data: Partial<HeroSectionData>): Promise<HeroSectionData> {
    try {
      const docRef = doc(db, 'storefront', this.HERO_SECTION_DOC);
      const docSnap = await getDoc(docRef);
      
      let heroData: HeroSectionData;
      
      if (docSnap.exists()) {
        // Update existing document
        const existingData = docSnap.data();
        heroData = {
          id: docSnap.id,
          ...existingData,
          ...data,
          createdAt: existingData.createdAt?.toDate() || new Date(),
          updatedAt: new Date()
        } as HeroSectionData;
        
        await updateDoc(docRef, {
          ...data,
          updatedAt: serverTimestamp()
        });
      } else {
        // Create new document
        const newData = {
          ...createEmptyHeroSectionData(),
          ...data,
          id: this.HERO_SECTION_DOC,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        await setDoc(docRef, newData);
        
        heroData = {
          ...newData,
          createdAt: new Date(),
          updatedAt: new Date()
        } as HeroSectionData;
      }
      
      return heroData;
    } catch (error) {
      console.error('Error updating hero section data:', error);
      throw new Error('Failed to update hero section data');
    }
  }

  // Promotional Banner
  async getPromotionalBannerData(): Promise<PromotionalBannerData | null> {
    try {
      const docRef = doc(db, 'storefront', this.PROMOTIONAL_BANNER_DOC);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as PromotionalBannerData;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching promotional banner data:', error);
      throw new Error('Failed to fetch promotional banner data');
    }
  }

  async updatePromotionalBannerData(data: Partial<PromotionalBannerData>): Promise<PromotionalBannerData> {
    try {
      const docRef = doc(db, 'storefront', this.PROMOTIONAL_BANNER_DOC);
      const docSnap = await getDoc(docRef);
      
      let bannerData: PromotionalBannerData;
      
      if (docSnap.exists()) {
        // Update existing document
        const existingData = docSnap.data();
        bannerData = {
          id: docSnap.id,
          ...existingData,
          ...data,
          createdAt: existingData.createdAt?.toDate() || new Date(),
          updatedAt: new Date()
        } as PromotionalBannerData;
        
        await updateDoc(docRef, {
          ...data,
          updatedAt: serverTimestamp()
        });
      } else {
        // Create new document
        const newData = {
          ...createEmptyPromotionalBannerData(),
          ...data,
          id: this.PROMOTIONAL_BANNER_DOC,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        await setDoc(docRef, newData);
        
        bannerData = {
          ...newData,
          createdAt: new Date(),
          updatedAt: new Date()
        } as PromotionalBannerData;
      }
      
      return bannerData;
    } catch (error) {
      console.error('Error updating promotional banner data:', error);
      throw new Error('Failed to update promotional banner data');
    }
  }

  // Initialize default data from current App.tsx content
  async initializeDefaultData(): Promise<void> {
    try {
      // Check if data already exists
      const existingSections = await this.getHomepageProductSections();
      const existingHero = await this.getHeroSectionData();
      const existingBanner = await this.getPromotionalBannerData();

      // Initialize homepage product sections if none exist
      if (existingSections.length === 0) {
        const defaultSections = [
          {
            title: 'Birthday Cakes',
            description: 'Make your birthday extra special with a personalised cake from our Dublin home bakery. Pick from our gorgeous designs or let us design a custom cake that flawlessly fits your party\'s style.',
            buttonText: 'Shop Now',
            buttonLink: '/catalog/birthday-cakes',
            imageSrc: '/images/homepage-products/IMG_2.webp',
            imageAlt: 'Beautiful birthday cake with decorations',
            reverse: false,
            backgroundColor: 'pink' as const,
            order: 0,
            isActive: true
          },
          {
            title: 'Cake & Cupcake Set',
            description: 'Upgrade your celebrations with adorable cake and cupcake box sets from us. Pick from our charming designs or let us customise a mini cake to match your party theme perfectly!',
            buttonText: 'Shop Now',
            buttonLink: '/catalog/cake-&-cupcake-set',
            imageSrc: '/images/homepage-products/IMG_3.webp',
            imageAlt: 'Cake and cupcake set',
            reverse: true,
            backgroundColor: 'white' as const,
            order: 1,
            isActive: true
          },
          {
            title: 'Celebration Cakes',
            description: 'Make every occasion unforgettable with a custom celebration cake. From elegant tiered cakes to adorable cake designs, your dream cake is just a click (or a sprinkle) away. Start creating your sweet centerpiece today!',
            buttonText: 'Shop Now',
            buttonLink: '/catalog/celebration-cakes',
            imageSrc: '/images/homepage-products/IMG_4.webp',
            imageAlt: 'Celebration cake',
            reverse: false,
            backgroundColor: 'pink' as const,
            order: 2,
            isActive: true
          },
          {
            title: 'Wedding Cakes',
            description: 'Weddings are a memorable event and everyone deserves to celebrate with a special treat. Our job is to bring your idea to life and create the most magical piece for your special day.',
            buttonText: 'Contact us',
            buttonLink: '/contact',
            imageSrc: '/images/homepage-products/IMG_5.webp',
            imageAlt: 'Wedding cake',
            reverse: true,
            backgroundColor: 'white' as const,
            order: 3,
            isActive: true
          },
          {
            title: 'Custom Cake Flavors',
            description: 'Looking to try something new? Say no more, we got you! Unexpected flavors coming together to create magic then say no no more, we got you!',
            buttonText: 'Shop Now',
            buttonLink: '/catalog/custom-cake-flavours',
            imageSrc: '/images/homepage-products/IMG_6.webp',
            imageAlt: 'Custom flavored cake',
            reverse: false,
            backgroundColor: 'pink' as const,
            order: 4,
            isActive: true
          },

          {
            title: 'Desserts',
            description: 'Indulge in our sweet treats. From decadent chocolate cakes to refreshing fruit tarts, we have something for every occasion. Explore our collection of desserts and find your perfect sweet treat.',
            buttonText: 'Shop Now',
            buttonLink: '/catalog/desserts',
            imageSrc: '/images/homepage-products/IMG_8.webp',
            imageAlt: 'Desserts',
            reverse: true,
            backgroundColor: 'white' as const,
            order: 5,
            isActive: true
          },

          {
            title: 'Create Your Cake',
            description: 'Get Creative: Design Your Dream Cake! Customise every part, flavors, frosting, and decorations. Easily craft themed treats for any event. Your perfect cake, just how you imagine it—start designing today',
            buttonText: 'Shop Now',
            buttonLink: '/catalog/create-your-own-cake',
            imageSrc: '/images/homepage-products/IMG_7.webp',
            imageAlt: 'Create your own cake',
            reverse: true,
            backgroundColor: 'white' as const,
            order: 6,
            isActive: true
          }
        ];

        for (const section of defaultSections) {
          await this.createHomepageProductSection(section);
        }
      }

      // Initialize hero section if none exists
      if (!existingHero) {
        await this.updateHeroSectionData({
          imageSrc: '/images/homepage-products/IMG_1.webp',
          imageAlt: 'The Frosting Room Hero Image',
          isActive: true
        });
      }

      // Initialize promotional banner if none exists
      if (!existingBanner) {
        await this.updatePromotionalBannerData({
          message: '10% off all orders placed in month of September',
          code: 'TFR25',
          dates: 'September 20-23 and October 5-23',
          note: 'Please note we are also unavailable to take orders on these dates',
          isActive: true
        });
      }
    } catch (error) {
      console.error('Error initializing default storefront data:', error);
      throw new Error('Failed to initialize default storefront data');
    }
  }
}

export const storefrontService = new StorefrontService();
